import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import MapLocation from '@app/models/map-location';
import Position from '@app/models/position';
import { Observable } from 'rxjs';

import { LogService } from './log.service';
import { NotificationService } from './notification.service';
import { AppSettingsService } from './app-settings.service'
/* NG6+ 
@Injectable({
  providedIn: 'root',
})
*/

/**
 * A service of misc mapping and location methods.
 * See open-layer* for OL-specific methods.
 * See map-configuration.service for map configuration methods.
 */
@Injectable()
export class MapLocationService {
  private readonly _serviceName = 'MapLocationService';

  /*
    Initial Default Map Position - for when the user presses "Search Listings" without specifying an activity or location.
  
    This map center puts both east texas and mid-atlantic in the map view w/ a zoom level of 4 (on a mobile screen)
  */
  private _initialMapZoom = 4;
  private _initialMapCenterLat = 39.138914911426866;
  private _initialMapCenterLon = -85.000121921875;

  // a position to set and use as an override for map positioning data
  private _defaultMapOverridePosition: Position = {
    latitude: this._initialMapCenterLat,
    longitude: this._initialMapCenterLon,
    zoom: this._initialMapZoom,
    timestamp: null,
    coords: null,
    altitude: null,
    srid: null,
  } as Position;

  constructor(private http: HttpClient, private log: LogService, private notify: NotificationService, readonly appSettings: AppSettingsService,
    ) {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * A system default map override position. Provides a position and zoom level  that  puts both east texas and mid-atlantic in the map view w/ a zoom level of 4 (on a mobile screen)
   */
  public get defaultMapOverridePosition(): Position {
    return this._defaultMapOverridePosition;
  }

  /**
   * Gets a default map override position
   */
  public getDefaultMapOverridePosition(): Position {
    return {
      latitude: this._initialMapCenterLat,
      longitude: this._initialMapCenterLon,
      zoom: this._initialMapZoom,
      timestamp: null,
      coords: null,
      altitude: null,
      srid: null,
    } as Position;
  }

  public navigatorGeolocationWarningMessage =
    'Your location could not be determined. Check your device settings to verify Location services are enabled.';

  /**
   * Gets the map location from the API (see file mapping\initial-map-locations.json in order to modify)
   */
  public getMapLocations(): Observable<MapLocation[]> {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.getMapLocations()`);
    }

    return this.http.get<MapLocation[]>('public/maplocation');
  }

  /**
   * Property indicating if the browser can obtain geolocate position
   */
  public get canGeolocate(): boolean {
    if (navigator.geolocation) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Reverse geocodes a coordinate (lat long)
   * @param x X coordinate or Longitude value
   * @param y Y coordinate or Latitude value
   * @returns
   */
  public reverseGeocode(x: number, y: number): Observable<any> {
    return this.http.get<any>(`public/location/geocode/reverse/${x}/${y}/`);
  }

  public googleReverseCode(x: number, y: number): Observable<any> {
    let googleMapsKey = this.appSettings.googleMapsKey
    return this.http.get<any>(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${x},${y}&result_type=street_address&key=${googleMapsKey}`)
  }
}
